import 'twin.macro';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import _isEmpty from 'lodash-es/isEmpty';
import Textarea from '@teetsh/app/src/components/Form/Textarea';
import _debounce from 'lodash-es/debounce';
import { PlusSmallIcon as PlusSm } from '@heroicons/react/24/outline';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { useTranslation } from 'react-i18next';
import { GlobalRemarkAIWrapper } from '../GlobalRemark/GlobalRemarkAIWrapper';

interface Props {
  value: string;
  onChange: (p: string) => void;
  readonly?: boolean;
  livretEleveId: string;
  matiereId: string;
  withIA?: boolean;
}

export function Remark(props: Props) {
  const { onChange, readonly, withIA } = props;
  const [value, setValue] = useState<string>(props.value);
  const [isEditing, setIsEditing] = useState(false);
  const [isAILoading, setIsAILoading] = useState(false);
  const { t } = useTranslation(['common']);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const debouncedSave = useCallback(
    _debounce(nextValue => onChange(nextValue), 500),
    [onChange],
  );
  useEffect(() => {
    // this useeffect is required for sync feature
    if (!isEditing && props.value !== value) {
      // make sure to set state only if we are not in edit mode to prevent race conditions
      setValue(props.value);
    }
  }, [props.value]);

  const handleChange = nextValue => {
    setValue(nextValue);
    debouncedSave(nextValue);
  };
  if (readonly) {
    return <div tw="whitespace-pre-line">{value}</div>;
  }
  if (_isEmpty(value) && !isEditing) {
    return (
      <Button
        minimal
        small
        fill
        tw="text-muted"
        onClick={() => {
          setIsEditing(true);
          setTimeout(() => {
            if (inputRef?.current) {
              inputRef.current.focus();
            }
          }, 10);
        }}
        icon={PlusSm}
      >
        {t('common:buttons.add')}
      </Button>
    );
  }
  if (withIA) {
    return (
      <GlobalRemarkAIWrapper
        value={value}
        visible={isEditing}
        livretEleveId={props.livretEleveId}
        matiereId={props.matiereId}
        onLoadingStateChange={setIsAILoading}
        onCancel={() => {
          setIsEditing(false);
        }}
        small
        onChange={v => {
          setIsEditing(false);
          handleChange(v);
        }}
      >
        <Textarea
          withCount
          removeHandle
          maxLength={1000}
          onFocus={() => setIsEditing(true)}
          onBlur={() => {
            if (!isAILoading) {
              setTimeout(() => {
                setIsEditing(false);
              }, 100);
            }
          }}
          inputRef={inputRef}
          value={value}
          tw="pb-12"
          onChange={e => handleChange(e.currentTarget.value)}
        />
      </GlobalRemarkAIWrapper>
    );
  }
  return (
    <Textarea
      withCount
      removeHandle
      maxLength={1000}
      onFocus={() => {
        setIsEditing(true);
      }}
      onBlur={() => {
        setIsEditing(false);
      }}
      inputRef={inputRef}
      value={value}
      onChange={e => handleChange(e.currentTarget.value)}
    />
  );
}
